// src/components/tareas/tareasModal.js
import React, { useEffect, useRef, forwardRef } from "react";
import { X, CheckCircle, HelpCircle, Gamepad2 } from "lucide-react";

const primaryBtnStyle = {
  padding: "10px 16px",
  borderRadius: 10,
  border: "1px solid #31bf52ff",
  background: "#349f38ff",
  color: "white",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
  fontWeight: 700,
  fontSize: "0.9rem",
  fontFamily: "inherit",
  letterSpacing: "0.01em",
};
export const StartActivityButton = forwardRef(function StartActivityButton(
  { onStart, label = "Iniciar actividad", className, style, isDone, isLocked, disabled },
  ref
) {
  return (
    <button
      type="button"
      ref={ref}
      onClick={() => !isLocked && onStart?.()}
      className={className}
      disabled={disabled}
      style={{
        ...primaryBtnStyle,
        background: isLocked ? "#94a3b8" : isDone ? "#16a34a" : "#2563eb",
        cursor: isLocked ? "not-allowed" : "pointer",
        opacity: isLocked ? 0.7 : 1,
        ...(style || {}),
      }}
    >
      <CheckCircle size={18} /> {label}
    </button>
  );
});

export default function TareasModal({ open, onClose, tarea, onStart, actividades }) {
  const startBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => startBtnRef.current?.focus(), 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open || !tarea) return null;

  // Contar preguntas y juegos
  const children = tarea.children || actividades || [];
  const preguntas = children.filter(
    (a) => a?.tipo === "actividad" && a?.subtipo !== "juego"
  ).length;
  const juegos = children.filter(
    (a) => a?.tipo === "actividad" && a?.subtipo === "juego"
  ).length;

  // Estado de la tarea (se infiere desde props si están disponibles)
  const isDone = tarea._done ?? false;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tareas-modal-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: 20,
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: 20,
          width: "100%",
          maxWidth: 460,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,.18)",
          animation: "modalIn .18s ease-out",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ── Header coloreado ── */}
        <div style={{
          background: isDone ? "#16a34a" : "#2563eb",
          padding: "18px 20px 16px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              background: "rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
              <CheckCircle size={20} color="white" />
            </div>
            <div>
              <div style={{ color: "white", fontWeight: 800, fontSize: "1rem", lineHeight: 1.2 }}>
                Detalles de Tarea
              </div>
              <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.78rem", fontWeight: 600, marginTop: 2 }}>
                {isDone ? "Completada" : "Pendiente"}
              </div>
            </div>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: 0,
              cursor: "pointer",
              padding: 6,
              borderRadius: 8,
              color: "white",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <X size={18} color="white" />
          </button>
        </div>

        {/* ── Cuerpo ── */}
        <div style={{ padding: "20px 20px 8px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Título */}
          <h3 id="tareas-modal-title" style={{ margin: 0, fontSize: "1.1rem", fontWeight: 900, color: "#111827" }}>
            {tarea.nombre?.toUpperCase()}
          </h3>

          {/* Chips preguntas / juegos */}
          {(preguntas > 0 || juegos > 0) && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {preguntas > 0 && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "#eff6ff", border: "1px solid #bfdbfe",
                  borderRadius: 8, padding: "5px 10px",
                  fontSize: "0.78rem", fontWeight: 700, color: "#1d4ed8",
                }}>
                  <HelpCircle size={14} /> {preguntas} Preguntas
                </div>
              )}
              {juegos > 0 && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "#f5f3ff", border: "1px solid #ddd6fe",
                  borderRadius: 8, padding: "5px 10px",
                  fontSize: "0.78rem", fontWeight: 700, color: "#6d28d9",
                }}>
                  <Gamepad2 size={14} /> {juegos} Juegos
                </div>
              )}
            </div>
          )}

          {/* Descripción */}
          {tarea.descripcion && (
            <div style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "12px 14px",
              color: "#4b5563",
              fontSize: "0.88rem",
              lineHeight: 1.6,
            }}>
              {tarea.descripcion}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex",
          gap: 10,
          padding: "12px 20px 20px",
          justifyContent: "flex-end",
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: "10px 16px",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
            }}
          >
            Cerrar
          </button>
          <button
            ref={startBtnRef}
            type="button"
            onClick={onStart}
            style={{
              flex: 1,
              padding: "10px 16px",
              borderRadius: 12,
              border: "none",
              background: isDone ? "#16a34a" : "#2563eb",
              color: "white",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <CheckCircle size={16} />
            {isDone ? "Reintentar" : "Iniciar"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { transform: translateY(10px) scale(0.98); opacity: 0 }
          to   { transform: translateY(0)    scale(1);    opacity: 1 }
        }
      `}</style>
    </div>
  );
}